/**
 * Courant politique — classification grand public en 6 familles.
 *
 * Deuxième étage du système de profil (2026-06-12, demande Mathias) :
 * au-dessus des archétypes fins (`classifyArchetypeKey`, 15 silhouettes),
 * une famille lisible par tout le monde : gauche, droite, écologiste,
 * centre, extrême gauche, extrême droite. Le courant est affiché au
 * cockpit (bandeau) et croisé avec le RÉSULTAT du mandat au T60 pour
 * produire le label illustratif final.
 *
 * Doctrine du score gauche/droite (à la française) : l'axe SOCIÉTAL pèse
 * plus que l'économique — c'est précisément la leçon du bug « souverainiste
 * de gauche » : une économie interventionniste n'a jamais suffi à classer
 * quelqu'un à gauche. Le souverainisme teinte à droite (modéré), sauf
 * quand le reste du profil est progressiste.
 */

import type { PoliticalProfile } from "./types";

export type CourantKey =
  | "extreme_gauche"
  | "gauche"
  | "vert"
  | "centre"
  | "droite"
  | "extreme_droite";

export const COURANT_NAME: Record<CourantKey, string> = {
  extreme_gauche: "Extrême gauche",
  gauche: "Gauche",
  vert: "Écologiste",
  centre: "Centre",
  droite: "Droite",
  extreme_droite: "Extrême droite",
};

const AXIS_THRESHOLD = 0.1;

export function classifyCourant(p: PoliticalProfile): CourantKey {
  const eco = p.economic;
  const soc = p.societal;
  const intl = p.international;
  const aut = p.authority;

  const maxAbs = Math.max(
    Math.abs(eco),
    Math.abs(soc),
    Math.abs(intl),
    Math.abs(aut),
  );
  if (maxAbs < AXIS_THRESHOLD) return "centre";

  // Écologiste — la silhouette de l'écologie politique : progressiste,
  // interventionniste MODÉRÉ (l'ultra-étatisme bascule à l'extrême
  // gauche), libertaire. Testée avant le score gauche/droite : c'est
  // une famille à part, pas une nuance de gauche.
  if (
    soc < -AXIS_THRESHOLD &&
    aut < -AXIS_THRESHOLD &&
    eco < -AXIS_THRESHOLD &&
    eco > -0.5
  ) {
    return "vert";
  }

  // Score gauche(−)/droite(+) : sociétal 50 %, économie 35 %,
  // souverainisme 15 %.
  const lr = 0.5 * soc + 0.35 * eco + 0.15 * intl;

  if (lr >= 0.06) {
    // Extrême droite = la radicalité identitaire/autoritaire, PAS
    // l'ultra-libéralisme économique (un thatchérien reste « droite »).
    return soc >= 0.45 || aut >= 0.5 ? "extreme_droite" : "droite";
  }
  if (lr <= -0.06) {
    // Extrême gauche = la radicalité économique (rupture étatiste)
    // ou l'autoritarisme révolutionnaire.
    return eco <= -0.5 || aut >= 0.5 ? "extreme_gauche" : "gauche";
  }
  return "centre";
}

// ---------------------------------------------------------------------------
// Labels illustratifs — courant × résultat du mandat (T60)
// ---------------------------------------------------------------------------

/** Palier de sortie du mandat, déjà calculé par MandateEndOverlay. */
export type MandateOutcome = "glory" | "contested" | "failed";

/**
 * Le label qui illustre le joueur en une formule : son courant, teinté
 * par la façon dont le mandat s'est terminé. C'est la ligne en gros
 * caractères de la signature historique.
 */
export const COURANT_OUTCOME_LABELS: Record<
  CourantKey,
  Record<MandateOutcome, string>
> = {
  extreme_gauche: {
    glory: "Le Tribun du peuple",
    contested: "Le Robespierre assagi",
    failed: "La Révolution inachevée",
  },
  gauche: {
    glory: "L'Héritier de Jaurès",
    contested: "Le Mitterrand des fractures",
    failed: "La Rose fanée",
  },
  vert: {
    glory: "Le Président climat",
    contested: "L'Écologiste de gouvernement",
    failed: "La Promesse verte trahie",
  },
  centre: {
    glory: "Le Sage de l'Élysée",
    contested: "L'Équilibriste",
    failed: "Le Centre introuvable",
  },
  droite: {
    glory: "Le Père de la Nation",
    contested: "Le Réformateur contesté",
    failed: "La Droite essoufflée",
  },
  extreme_droite: {
    glory: "L'Homme fort — l'Histoire jugera",
    contested: "Le Tribun des frontières",
    failed: "L'Aventure nationale échouée",
  },
};
